class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // Store bound handlers for proper cleanup
    this.boundDebouncedOnSubmit = this.debouncedOnSubmit.bind(this);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.boundDebouncedOnSubmit);
    facetForm.addEventListener('change', this.boundDebouncedOnSubmit);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    
    // Attach sorting listeners
    this.attachSortingListeners();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static _currentProductCount() {
    const el = document.getElementById('ProductCount') || document.getElementById('ProductCountDesktop');
    const nums = el ? (el.textContent || '').replace(/,/g, '').match(/\d+/g) : null;
    return nums ? Math.max.apply(null, nums.map(Number)) : null;
  }

  // Dispatch shopify:search:update (/search) or shopify:collection:update
  // (/collections/...) when filters/sort change. The deferred promise resolves with
  // { totalCount } (search) or { productsCount } (collection) once the grid re-renders
  // (see renderProductCount). sortKey carries only the FIELD (mirrors Storefront
  // SortKeys); direction is the separate `reverse` boolean (price-ascending vs
  // -descending -> reverse false/true).
  static maybeDispatchFacetUpdate(searchParams) {
    try {
      const SE = window.StandardEvents;
      if (!SE) return;
      const path = window.location.pathname;
      const isSearch = path.indexOf('/search') !== -1;
      const isCollection = path.indexOf('/collections/') !== -1;
      if (!isSearch && !isCollection) return;
      const Ctor = isSearch ? SE.SearchUpdateEvent : SE.CollectionUpdateEvent;
      if (!Ctor) return;

      const params = new URLSearchParams(searchParams || '');
      const sortMap = {
        'relevance': { sortKey: 'RELEVANCE' },
        'price-ascending': { sortKey: 'PRICE', reverse: false },
        'price-descending': { sortKey: 'PRICE', reverse: true },
        'created-ascending': { sortKey: 'CREATED', reverse: false },
        'created-descending': { sortKey: 'CREATED', reverse: true },
        'best-selling': { sortKey: 'BEST_SELLING' },
        'title-ascending': { sortKey: 'TITLE', reverse: false },
        'title-descending': { sortKey: 'TITLE', reverse: true },
        'manual': { sortKey: 'MANUAL' },
      };
      const filters = typeof Ctor.parseProductFilters === 'function' ? (Ctor.parseProductFilters(params) || []) : [];
      const rawSort = params.get('sort_by');
      const mapped = rawSort && sortMap[rawSort];

      let payload;
      let countKey;
      if (isSearch) {
        const search = { query: params.get('q') || '', productFilters: filters };
        if (mapped) { search.sortKey = mapped.sortKey; if (mapped.reverse != null) search.reverse = mapped.reverse; }
        payload = { search: search };
        countKey = 'totalCount';
      } else {
        const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page;
        const handle = (path.split('/collections/')[1] || '').split('/')[0].split('?')[0];
        const pc = FacetFiltersForm._currentProductCount();
        payload = {
          collection: {
            id: (meta && meta.resourceType === 'collection' && meta.resourceId) ? meta.resourceId : null,
            handle: handle,
            productsCount: pc == null ? 0 : pc,
          },
          productFilters: filters,
        };
        if (mapped) { payload.sortKey = mapped.sortKey; if (mapped.reverse != null) payload.reverse = mapped.reverse; }
        countKey = 'productsCount';
      }

      const deferred = Ctor.createPromise();
      payload.promise = deferred.promise;
      const container = document.getElementById('ProductGridContainer') || document.querySelector('collection-component, main') || document.body;
      container.dispatchEvent(new Ctor(payload));
      const pending = { deferred: deferred, countKey: countKey, timer: null };
      FacetFiltersForm._pendingFacetUpdate = pending;
      // Safety: never leave the promise hanging if the re-render path doesn't run.
      // Cleared in renderProductCount on the normal path.
      pending.timer = setTimeout(function () {
        if (FacetFiltersForm._pendingFacetUpdate === pending) {
          const o = {}; o[countKey] = 0;
          try { deferred.resolve(o); } catch (e) { /* noop */ }
          FacetFiltersForm._pendingFacetUpdate = null;
        }
      }, 5000);
    } catch (e) {
      console.warn('[events] facet update emit failed', e);
    }
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.maybeDispatchFacetUpdate(searchParams);
    // For collection_filtered (js/sections/analytics.js). fromHistory: back/forward
    // restoring a state, not the customer using a control.
    document.dispatchEvent(new CustomEvent('facets:change', {
      detail: { searchParams: searchParams || '', fromHistory: !updateURLHash },
    }));
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
    if (document.querySelector('.js-load-more') || document.querySelector('.js-infinite-scroll')) 
      loadMore();
    
    colorSwatches();
  }

  static renderProductCount(html) {
    let totalCount = null;
    try {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const productCountElement = parsedHTML.getElementById('ProductCount');

      if (productCountElement) {
        const count = productCountElement.innerHTML;
        const nums = (count || '').replace(/,/g, '').match(/\d+/g);
        if (nums) totalCount = Math.max.apply(null, nums.map(Number));

        const container = document.getElementById('ProductCount');
        const containerDesktop = document.getElementById('ProductCountDesktop');
        if (container) {
          container.innerHTML = count;
          container.classList.remove('loading');
        }
        if (containerDesktop) {
          containerDesktop.innerHTML = count;
          containerDesktop.classList.remove('loading');
        }
      } else {
        console.warn('ProductCount element not found in response');
      }
    } catch (error) {
      console.error('Error updating product count:', error);
    }

    // Resolve the pending search:update / collection:update promise with the count.
    if (FacetFiltersForm._pendingFacetUpdate) {
      const pending = FacetFiltersForm._pendingFacetUpdate;
      if (pending.timer) clearTimeout(pending.timer);
      try {
        const result = {};
        result[pending.countKey] = totalCount == null ? 0 : totalCount;
        pending.deferred.resolve(result);
      } catch (e) { /* best-effort */ }
      FacetFiltersForm._pendingFacetUpdate = null;
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    
    // Re-attach event listeners to sorting selects after re-render
    document.querySelectorAll('facet-filters-form').forEach(form => {
      form.attachSortingListeners();
    });
    
    // Update mobile sort form with current active filters
    this.updateMobileSortForm();
    
    // Trigger custom event for show more/less functionality
    document.dispatchEvent(new CustomEvent('facetFiltersForm:updated'));
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    
    // Handle mobile checkbox filters
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
      return;
    }
    
    // Handle sorting (both desktop and mobile)
    if (event.target.name === 'sort_by') {
      const currentForm = event.target.closest('form');
      const searchParams = this.createSearchParams(currentForm);
      this.onSubmitForm(searchParams, event);
      return;
    }
    
    // Handle checkbox filter changes (both desktop and mobile)
    if (event.target.type === 'checkbox') {
      const currentForm = event.target.closest('form');
      const searchParams = this.createSearchParams(currentForm);
      this.onSubmitForm(searchParams, event);
      return;
    }
    
    // Handle other form inputs
    const forms = [];
    const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm' || form.id === 'FacetSortFormMobile') {
          const noJsElements = document.querySelectorAll('.no-js-list');
          noJsElements.forEach((el) => el.remove());
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      }
    });
    
    // Update mobile sort form with current filters before submitting
    FacetFiltersForm.updateMobileSortForm();
    
    this.onSubmitForm(forms.join('&'), event)
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  attachSortingListeners() {
    const sortSelects = this.querySelectorAll('select[name="sort_by"]');
    sortSelects.forEach(select => {
      // Remove existing listeners to prevent duplicates
      select.removeEventListener('change', this.boundDebouncedOnSubmit);
      select.addEventListener('change', this.boundDebouncedOnSubmit);
    });
  }

  static updateMobileSortForm() {
    const mobileSortForm = document.getElementById('FacetSortFormMobile');
    const mobileFiltersForm = document.getElementById('FacetFiltersFormMobile');
    
    if (!mobileSortForm || !mobileFiltersForm) return;
    
    // Get all active filter inputs from mobile filters form
    const activeFilterInputs = mobileFiltersForm.querySelectorAll('input[type="checkbox"]:checked, input[name*="filter.v.price.gte"], input[name*="filter.v.price.lte"]');
    
    // Remove existing filter inputs from mobile sort form (except q and options[prefix])
    const existingFilterInputs = mobileSortForm.querySelectorAll('input[type="hidden"]:not([name="q"]):not([name="options[prefix]"])');
    existingFilterInputs.forEach(input => input.remove());
    
    // Add current active filter inputs to mobile sort form
    activeFilterInputs.forEach(input => {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = input.name;
      hiddenInput.value = input.value;
      mobileSortForm.appendChild(hiddenInput);
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  fillSlider () {
    const inputRangeWrappers = document.querySelectorAll('.facets__price .facets__range');
    inputRangeWrappers.forEach(inputWrapper => {
      const inputsRange = inputWrapper.querySelectorAll('.field__range');
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];

      const range = inputRangeTo.max - inputRangeTo.min;
      const fromCurrent = inputRangeFrom.value - inputRangeTo.min;
      const toCurrent = inputRangeTo.value - inputRangeTo.min;
      const minRange = fromCurrent / range * 100;
      const maxRange = toCurrent / range * 100;

      inputWrapper.setAttribute(
        'style',
        `--range-min: ${minRange}%; --range-max: ${maxRange}%`
      );
    })
  }

  controlSlider () {
    const inputRangeWrappers = document.querySelectorAll('.facets__price .facets__range');
    const inputNumberWrappers = document.querySelectorAll('.facets__price .facets__price-wrapper');

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll('.field__range');
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom = inputNumberWrappers[index].querySelectorAll('.field__input')[0];
      const inputNumberTo = inputNumberWrappers[index].querySelectorAll('.field__input')[1];

      inputRangeFrom.oninput = () => {
        const from = parseInt(inputRangeFrom.value, 10);
        const to = parseInt(inputRangeTo.value, 10);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        }
        else {
          inputNumberFrom.value = from;
        }

        this.fillSlider();
      }

      if (Number(inputRangeTo.value) <= 0 ) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = parseInt(inputRangeFrom.value, 10);
        const to = parseInt(inputRangeTo.value, 10);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        }
        else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0 ) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }

        this.fillSlider();
      }
    })
  }

  controlInput() {
    const inputRangeWrappers = document.querySelectorAll('.facets__price .facets__range');
    const inputNumberWrappers = document.querySelectorAll('.facets__price .facets__price-wrapper');

    inputNumberWrappers.forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll('.field__input');
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom = inputRangeWrappers[index].querySelectorAll('.field__range')[0];
      const inputRangeTo = inputRangeWrappers[index].querySelectorAll('.field__range')[1];

      inputNumberFrom.oninput = () => {
        const from = parseInt(inputNumberFrom.value, 10);
        const to = parseInt(inputNumberTo.value, 10);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } 
        else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      }

      inputNumberTo.oninput = () => {
        const from = parseInt(inputNumberFrom.value, 10);
        const to = parseInt(inputNumberTo.value, 10);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        }
        else {
          inputNumberTo.value = from;
        }

        this.fillSlider();
      }

    })
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Facets show more/less functionality
document.addEventListener('DOMContentLoaded', function() {
  initializeFacetsShowMore();
});

function initializeFacetsShowMore() {
  // Add event listeners to all show more/less buttons
  document.addEventListener('click', function(event) {
    // Handle mobile facets show more/less (if implemented)
    if (event.target.closest('.mobile-facets__show-more-btn')) {
      event.preventDefault();
      event.stopPropagation();
      const button = event.target.closest('.mobile-facets__show-more-btn');
      const facetListId = button.getAttribute('data-facet-list');
      const facetList = document.querySelector(`[data-facet-list-mobile="${facetListId}"]`);
      
      if (facetList) {
        toggleFacetItems(facetList, button, 'mobile');
      }
    }
    
    // Handle desktop facets show more/less
    if (event.target.closest('.facets__show-more-btn')) {
      event.preventDefault();
      event.stopPropagation();
      const button = event.target.closest('.facets__show-more-btn');
      const facetListId = button.getAttribute('data-facet-list');
      const facetList = document.querySelector(`#Facet-${facetListId} .facets__list`);
      
      if (facetList) {
        toggleFacetItems(facetList, button, 'desktop');
      }
    }
  });
}

function toggleFacetItems(facetList, button, type) {
  const isMobile = type === 'mobile';
  const hiddenClass = isMobile ? 'mobile-facets__item--hidden' : 'facets__item--hidden';
  const itemClass = isMobile ? 'mobile-facets__item' : 'facets__item';
  
  const hiddenItems = facetList.querySelectorAll(`.${hiddenClass}`);
  const showMoreText = button.querySelector('.show-more-text');
  const showLessText = button.querySelector('.show-less-text');
  const isShowingMore = button.getAttribute('data-show-more') === 'true';
  
  if (isShowingMore) {
    // Show all items
    hiddenItems.forEach(item => {
      item.classList.remove(hiddenClass);
    });
    showMoreText.style.display = 'none';
    showLessText.style.display = 'inline-block';
    button.setAttribute('data-show-more', 'false');
  } else {
    // Hide items after the 5th one
    const allItems = facetList.querySelectorAll(`.${itemClass}`);
    allItems.forEach((item, index) => {
      if (index >= 5) {
        item.classList.add(hiddenClass);
      }
    });
    showMoreText.style.display = 'inline-block';
    showLessText.style.display = 'none';
    button.setAttribute('data-show-more', 'true');
  }
}

// Re-initialize after AJAX updates
document.addEventListener('facetFiltersForm:updated', function() {
  // Add a small delay to ensure DOM is fully updated
  setTimeout(() => {
    // Reset all show more/less buttons to their initial state after AJAX updates
    resetShowMoreButtons();
  }, 100);
});

function resetShowMoreButtons() {
  // Reset desktop show more/less buttons
  const desktopButtons = document.querySelectorAll('.facets__show-more-btn');
  
  desktopButtons.forEach(button => {
    const facetListId = button.getAttribute('data-facet-list');
    const facetList = document.querySelector(`#Facet-${facetListId} .facets__list`);
    
    if (facetList) {
      const allItems = facetList.querySelectorAll('.facets__item');
      const showMoreText = button.querySelector('.show-more-text');
      const showLessText = button.querySelector('.show-less-text');
      
      // Reset button state
      button.setAttribute('data-show-more', 'true');
      showMoreText.style.display = 'inline-block';
      showLessText.style.display = 'none';
      
      // Hide items after the 5th one
      allItems.forEach((item, index) => {
        if (index >= 5) {
          item.classList.add('facets__item--hidden');
        } else {
          item.classList.remove('facets__item--hidden');
        }
      });
    }
  });
  
  // Reset mobile show more/less buttons (if they exist)
  const mobileButtons = document.querySelectorAll('.mobile-facets__show-more-btn');
  
  mobileButtons.forEach(button => {
    const facetListId = button.getAttribute('data-facet-list');
    const facetList = document.querySelector(`[data-facet-list-mobile="${facetListId}"]`);
    
    if (facetList) {
      const allItems = facetList.querySelectorAll('.mobile-facets__item');
      const showMoreText = button.querySelector('.show-more-text');
      const showLessText = button.querySelector('.show-less-text');
      
      // Reset button state
      button.setAttribute('data-show-more', 'true');
      showMoreText.style.display = 'inline-block';
      showLessText.style.display = 'none';
      
      // Hide items after the 5th one
      allItems.forEach((item, index) => {
        if (index >= 5) {
          item.classList.add('mobile-facets__item--hidden');
        } else {
          item.classList.remove('mobile-facets__item--hidden');
        }
      });
    }
  });
}
